import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6be8b56a"),n=n(),_popScopeId(),n)
const _hoisted_1 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("div", { class: "ai-disclaimer" }, [
  /*#__PURE__*/_createElementVNode("i", null, "Artificial Intelligence can make mistakes. Please ensure you check before you apply.")
], -1))
const _hoisted_2 = { key: 0 }

import { computed, ref, watch, inject } from "vue";
import ModalBase from "@/components/ui/modals/modal-base.vue";
import { DxToolbarItem } from "devextreme-vue/popup";
import FieldValidationFreeTextArea from "../ui/inputs/field-validation-free-text-area.vue";
import AcquaintInfoBox from "../ui/inputs/acquaint-info-box.vue";
import displayErrorMessage from "@/utils/ErrorHandling";
import { getErrorMessage } from "bright-logic-core/src/errors";
import { AcquaintApiNextClient } from "@/services/AcquaintApiNextClient";
import { FieldSize } from "@/enums/system/FieldSize";
import { TextboxValueChangeEvent } from "@/enums/ui/inputs/TextboxValueChangeEvent";
import { AIPromptsToGenerate } from "@/enums/ai/AIPromptsToGenerate";
import { useSystemConfigurationStore } from "@/stores/SystemConfigurationStore";
import { SystemConfiguration } from "acquaint-api/constants/SystemConfiguration";


export default /*#__PURE__*/_defineComponent({
  __name: 'ai-text-generator-modal',
  props: {
  /**
   * The boolean value used with `v-model` to control the visibility or state of a modal or component.
   * @type {boolean}
   * @required
   */
  modelValue: {
    type: Boolean,
    required: true,
  },
  /**
   * The unique identifier for the property.
   * @type {number}
   * @default 0
   */
  propertyId: {
    type: Number,
    default: 0,
  },
  /** Type of prompt to generate */
  promptToGenerate: {
    type: Number,
    default: AIPromptsToGenerate.None,
  },
  /** Id of the property room to run the AI generator for. To use this option propertyId must be passed */
  roomId: {
    type: Number,
    default: 0,
  },
  /**
   * Determines whether to display apply button.
   * @type {boolean}
   * @default false
   */
  displayApplyButton: {
    type: Boolean,
    default: false,
  },
},
  emits: [
  /**
   * Triggered when the `modelValue` changes. This is used to update the v-model variable in the parent component.
   * @property {boolean} value - Indicates whether the modal has been opened or closed.
   */
  "update:modelValue",
  /** Triggered when the Apply button is clicked 
      @property response {string} AI generated text to be applied in the parent component */
  "applyAIResponse",
],
  setup(__props, { emit }) {

const props = __props;





const showModal = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit("update:modelValue", value),
});

const apiNextClient = inject("AcquaintApiNextClient") as AcquaintApiNextClient;
const request = ref("Write text about");
const aiResponse = ref("");
const aiCredits = ref<undefined | number>();
const firstAPICallMade = ref(false);
const loading = ref(false);
const outputCopiedToClipboard = ref(false);

const systemConfigurationStore = useSystemConfigurationStore();

watch(
  () => showModal.value,
  async () => {
    if (!showModal.value) {
      request.value = "Write text about";
      aiResponse.value = "";
      aiCredits.value = 0;
      firstAPICallMade.value = false;
      return;
    }
    fecthAICredits();

    switch (props.promptToGenerate) {
      case AIPromptsToGenerate.PropertyDescription:
        await fetchPropertyDescriptionPrompt();
        break;
      case AIPromptsToGenerate.PropertyRoomDescription:
        await fetchRoomDescriptionPrompt();
        break;
      case AIPromptsToGenerate.PropertyRoomTemplateDescription:
        await fetchRoomTemplateDescriptionPrompt();
        break;
    }
  },
);

const fecthAICredits = async () => {
  try {
    aiCredits.value = await apiNextClient.httpGet(
      "AIResponse/GetAICreditBalance",
    );
  } catch (err) {
    displayErrorMessage(getErrorMessage(err));
    aiCredits.value = undefined;
  }
};

const fetchPropertyDescriptionPrompt = async () => {
  try {
    request.value = await apiNextClient.httpGet(
      `AIPrompt/${props.propertyId}/Property`,
    );
  } catch (err) {
    request.value = "";
    displayErrorMessage(getErrorMessage(err));
  }
};

const fetchRoomDescriptionPrompt = async () => {
  /* For new rooms, there'll be no room description. This will cause the api to return a 404 response
   * Instead, we'll get the default prompt from the system config */
  if (props.roomId === 0) {
    await getDefaultRoomPrompt();
    return;
  }

  try {
    request.value = await apiNextClient.httpGet(
      `AIPrompt/${props.propertyId}/PropertyRoom/${props.roomId}`,
    );
  } catch (err) {
    request.value = "";
    displayErrorMessage(getErrorMessage(err));
  }
};

const fetchRoomTemplateDescriptionPrompt = async () => {
  /* For new room templates, there'll be no room description. This will cause the api to return a 404 response
   * Instead, we'll get the default prompt from the system config */
  if (props.roomId === 0) {
    await getDefaultRoomPrompt();
    return;
  }

  try {
    request.value = await apiNextClient.httpGet(
      `/AIPrompt/PropertyRoomTemplate/${props.roomId}`,
    );
  } catch (err) {
    request.value = "";
    displayErrorMessage(getErrorMessage(err));
  }
};

const getDefaultRoomPrompt = async () => {
  request.value = await systemConfigurationStore.getSystemConfigurationValue(
    SystemConfiguration.AIPropertyRoomDescriptionPrompt,
  );
  request.value = request.value.replace(
    new RegExp("\\[RoomDescription\\]", "i"),
    "",
  );
};

const fetchAIResponse = async () => {
  try {
    aiResponse.value = await apiNextClient.httpGet(
      `AIResponse?input=${request.value}`,
    );
    return true;
  } catch (err) {
    aiResponse.value = "";
    displayErrorMessage(getErrorMessage(err));
    return false;
  }
};

const fetchDisplayAIResponse = async () => {
  loading.value = true;

  if (await fetchAIResponse()) {
    firstAPICallMade.value = true;
    fecthAICredits();
  }

  loading.value = false;
};

watch(
  () => aiResponse.value,
  async () => {
    // Prevent blank text being written to the clipboard, as this may cause confusion with users
    if (!showModal.value || !aiResponse.value) {
      return;
    }

    try {
      await navigator.clipboard.writeText(aiResponse.value);
      outputCopiedToClipboard.value = true;
    } catch (err) {
      outputCopiedToClipboard.value = false;
    }
  },
);

const applyAIResponse = () => {
  emit("applyAIResponse", aiResponse.value);
  showModal.value = false;
};

const onCancelClick = () => (showModal.value = false);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(ModalBase, {
    modelValue: showModal.value,
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((showModal).value = $event)),
    title: "AI Text Generator",
    height: "600px",
    saving: loading.value
  }, {
    content: _withCtx(() => [
      _createVNode(AcquaintInfoBox, {
        text: aiCredits.value?.toString() ?? '0',
        label: "Credits Remaining",
        width: _unref(FieldSize).Small
      }, null, 8, ["text", "width"]),
      _createVNode(FieldValidationFreeTextArea, {
        modelValue: request.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((request).value = $event)),
        label: "Request",
        height: "6rem",
        "value-change-event": _unref(TextboxValueChangeEvent).KeyUp
      }, null, 8, ["modelValue", "value-change-event"]),
      _createVNode(FieldValidationFreeTextArea, {
        "read-only": !firstAPICallMade.value,
        modelValue: aiResponse.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((aiResponse).value = $event)),
        label: "Generated Text",
        height: "14rem",
        "value-change-event": _unref(TextboxValueChangeEvent).KeyUp
      }, null, 8, ["read-only", "modelValue", "value-change-event"]),
      _hoisted_1,
      (firstAPICallMade.value && outputCopiedToClipboard.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_2, " This text has been copied to the clipboard. "))
        : _createCommentVNode("", true)
    ]),
    footer: _withCtx(() => [
      _createVNode(_unref(DxToolbarItem), {
        visible: props.displayApplyButton,
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Apply Text',
          onClick: () => {
            applyAIResponse();
          },
          disabled: aiCredits.value == 0 || !firstAPICallMade.value || loading.value,
        }
      }, null, 8, ["visible", "options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Generate Text',
          onClick: () => {
            fetchDisplayAIResponse();
          },
          disabled: aiCredits.value == 0 || request.value == '' || loading.value,
        }
      }, null, 8, ["options"]),
      _createVNode(_unref(DxToolbarItem), {
        widget: "dxButton",
        toolbar: "bottom",
        location: "after",
        options: {
          text: 'Cancel',
          onClick: () => {
            onCancelClick();
          },
        }
      }, null, 8, ["options"])
    ]),
    _: 1
  }, 8, ["modelValue", "saving"]))
}
}

})